import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';
import bms_master from "../assests/bms/BMS Master.jpeg"
import bms_slave from "../assests/bms/BMS Slave.jpeg"
import VS1012 from "../assests/single-phase/VS1012.jpg"
import VS1048 from "../assests/single-phase/VS1048.jpeg"
import VS1424 from "../assests/single-phase/VS1424.jpg"
import VS2036_3048 from "../assests/single-phase/VS2036_3048.jpg"
import VS5048 from "../assests/single-phase/VS5048.jpg"
import VS5096 from "../assests/single-phase/VS5096.JPG"
import VS7512 from "../assests/single-phase/VS7512.jpg"
import VS8012 from "../assests/single-phase/VS8012.jpg"
import VS10180 from "../assests/single-phase/VS10180.jpg"
import CC24V from "../assests/solar/CC24V.jpeg"
import MPPT_48V96V from "../assests/solar/MPPT 48V96V.jpg"
import MPPT_12_24V from "../assests/solar/MPPT_12-24V.jpg"
import VS6072_3Phase from "../assests/three-phase/VS6072_3Phase.jpeg"
import VS10120_3Phase from "../assests/three-phase/VS10120_3Phase.jpg"
import { useNavigate } from 'react-router-dom';

const mockImages = {
  bms_master,
  bms_slave,
  VS1012,
  VS1048,
  VS1424,
  VS2036_3048,
  VS5048,
  VS5096,
  VS7512,
  VS8012,
  VS10180,
  CC24V,
  MPPT_48V96V,
  MPPT_12_24V,
  VS6072_3Phase,
  VS10120_3Phase
};

const productData = {
  "productCategories": [
    {
      "id": "bms",
      "title": "Battery Management System (BMS)",
      "description": "Advanced battery management solutions",
      "products": [
        {
          "id": 1,
          "name": "BMS Master Unit",
          "description": "Master control unit for battery management",
          "image": "bms_master"
        },
        {
          "id": 2,
          "name": "BMS Slave Unit",
          "description": "Slave module for distributed battery management",
          "image": "bms_slave"
        }
      ]
    },
    {
      "id": "single-phase",
      "title": "Single Phase Inverters",
      "description": "Reliable single-phase power conversion solutions",
      "products": [
        {
          "id": 3,
          "name": "VS1012",
          "description": "1000W 12V Single Phase Inverter",
          "image": "VS1012"
        },
        {
          "id": 4,
          "name": "VS1048",
          "description": "1000W 48V Single Phase Inverter",
          "image": "VS1048"
        },
        {
          "id": 5,
          "name": "VS1424",
          "description": "1400W 24V Single Phase Inverter",
          "image": "VS1424"
        },
        {
          "id": 6,
          "name": "VS2036/3048",
          "description": "2000W-3000W Inverter Series",
          "image": "VS2036_3048"
        },
        {
          "id": 7,
          "name": "VS5048",
          "description": "5000W 48V Single Phase Inverter",
          "image": "VS5048"
        },
        {
          "id": 8,
          "name": "VS5096",
          "description": "5000W 96V Single Phase Inverter",
          "image": "VS5096"
        },
        {
          "id": 9,
          "name": "VS7512",
          "description": "7500W 12V Single Phase Inverter",
          "image": "VS7512"
        },
        {
          "id": 10,
          "name": "VS8012",
          "description": "8000W 12V Single Phase Inverter",
          "image": "VS8012"
        },
        {
          "id": 11,
          "name": "VS10180",
          "description": "10000W 180V Single Phase Inverter",
          "image": "VS10180"
        }
      ]
    },
    {
      "id": "solar-controllers",
      "title": "Solar Charge Controllers",
      "description": "Efficient MPPT and PWM solar charge controllers",
      "products": [
        {
          "id": 12,
          "name": "CC24V",
          "description": "24V Solar Charge Controller",
          "image": "CC24V"
        },
        {
          "id": 13,
          "name": "MPPT 48V/96V",
          "description": "MPPT Controller for 48V/96V Systems",
          "image": "MPPT_48V96V"
        },
        {
          "id": 14,
          "name": "MPPT 12V/24V",
          "description": "MPPT Controller for 12V/24V Systems",
          "image": "MPPT_12_24V"
        }
      ]
    },
    {
      "id": "three-phase",
      "title": "Three Phase Inverters",
      "description": "Industrial-grade three-phase power solutions",
      "products": [
        {
          "id": 15,
          "name": "VS6072 Three Phase",
          "description": "6000W 72V Three Phase Inverter",
          "image": "VS6072_3Phase"
        },
        {
          "id": 16,
          "name": "VS10120 Three Phase",
          "description": "10000W 120V Three Phase Inverter",
          "image": "VS10120_3Phase"
        }
      ]
    }
  ],
  "technicalSpecs": {
    "default": [
      { "label": "Output Power AC Rated", "value": "Variable" },
      { "label": "AC Output Voltage", "value": "220V/240V/380V AC" },
      { "label": "Transfer Time", "value": "< 10 ms" },
      { "label": "AC Frequency", "value": "50 ± 15 Hz" },
      { "label": "Inverter Efficiency", "value": "90-96%" },
      { "label": "Battery Types Supported", "value": "Li-ion, Lead Acid, Gel" }
    ]
  }
};

const ProductsSection = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bms');
  const [currentView, setCurrentView] = useState('products');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailTab, setDetailTab] = useState('technical');

  // Get image URL from image key
  const getImageUrl = (imageKey) => {
    return mockImages[imageKey] || "/api/placeholder/300/200";
  };

  // Fixed: Remove the function call, just define the function
  const handleProduct = () => {
    navigate('/Products');
  };

  // Fixed: This function should also just navigate, not call another function
  const handleProductClick = () => {
    navigate('/Products');
  };

  const handleBackToProducts = () => {
    setCurrentView('products');
    setSelectedProduct(null);
  };

  const handleOrderNow = () => {
    setCurrentView('contact');
  };

  // Get current category data based on active tab
  const getCurrentCategory = () => {
    return productData.productCategories.find(cat => cat.id === activeTab);
  };

  const ProductCard = ({ product, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-[#C5D1D2] rounded-lg p-6 cursor-pointer hover:shadow-xl transition-all duration-300"
      whileHover={{ scale: 1.02, y: -5 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => handleProductClick(product)}
    >
      <div className="bg-gray-300 rounded-lg h-48 mb-4 flex items-center justify-center overflow-hidden">
        <img 
          src={getImageUrl(product.image)} 
          alt={product.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
        <Cpu className="w-20 h-20 text-gray-500" style={{display: 'none'}} />
      </div>
      <h3 className="text-xl text-center font-semibold text-gray-800 mb-2">{product.name}</h3>
      <p className="text-gray-600 text-sm text-center">{product.description}</p>
    </motion.div>
  );

  const ProductsView = () => {
    const currentCategory = getCurrentCategory();
    
    return (
      <section className="py-2 bg-white mt-8">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center text-gray-800 mb-12"
          >
            Our Products
          </motion.h2>

          {/* Fixed Tabs */}
      <motion.div 
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, delay: 0.2 }}
  className="flex flex-wrap justify-center mb-12 gap-2 sm:gap-3"
>
  {productData.productCategories.map((category) => (
    <button
      key={category.id}
      onClick={handleProduct}
      className={`w-full sm:w-auto px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-medium transition-all duration-300 ${
        activeTab === category.id
          ? 'bg-[#0572AF] text-white shadow-lg'
          : 'bg-[#0572AF] text-white shadow-lg'
      }`}
    >
      {category.title}
    </button>
  ))}
</motion.div>

        </div>
      </section>
    );
  };

  return (
    <AnimatePresence mode="wait">
      {currentView === 'products' && <ProductsView key="products" />}
      {/* {currentView === 'detail' && <ProductDetailView key="detail" />}
      {currentView === 'contact' && <ContactView key="contact" />} */}
    </AnimatePresence>
  );
};

export default ProductsSection;